import React from "react";

import { cn } from "../../utils/cn";
import { IndicationUnit } from "../Metrics/IndicationUnit";

export type LifecycleStatusTone = "success" | "pending" | "error" | "disabled";
export type OutcomeTone = "success" | "warning" | "danger" | "neutral";
export type BoundaryTone = "delegated" | "constrained" | "locked";
export type CountTone = "attention" | "neutral";
export type AlertSeverity = "critical" | "review" | "info";

interface SemanticBadgeProps {
    children: React.ReactNode;
    className?: string;
}

interface LifecycleStatusBadgeProps extends SemanticBadgeProps {
    state: LifecycleStatusTone;
}

interface OutcomeBadgeProps extends SemanticBadgeProps {
    tone: OutcomeTone;
}

interface BoundaryBadgeProps extends SemanticBadgeProps {
    boundary: BoundaryTone;
}

interface CountChipProps extends SemanticBadgeProps {
    tone: CountTone;
}

interface AlertBadgeProps extends SemanticBadgeProps {
    severity: AlertSeverity;
}

const SEMANTIC_BADGE_BASE =
    "inline-flex h-6 max-w-full cursor-default select-none items-center gap-1.5 whitespace-nowrap rounded-none border-l-2 px-2 font-sans text-xs leading-4 tracking-normal transition-colors duration-150";
const NEUTRAL_BADGE_SURFACE =
    "bg-surface-raised group-hover:bg-surface-ground group-hover/listitem:bg-surface-ground";

export function LifecycleStatusBadge({
    state,
    children,
    className
}: LifecycleStatusBadgeProps): React.ReactElement {
    const textClass = {
        success: "text-success-base",
        pending: "text-warning-base",
        error: "text-error-base",
        disabled: "text-foreground-muted"
    }[state];
    const borderClass = {
        success: "border-l-success-base",
        pending: "border-l-warning-base",
        error: "border-l-error-base",
        disabled: "border-l-line-dark"
    }[state];

    return (
        <span
            className={cn(
                SEMANTIC_BADGE_BASE,
                NEUTRAL_BADGE_SURFACE,
                borderClass,
                className
            )}
        >
            <IndicationUnit variant="status" state={state} size={7} />
            <span className={textClass}>{children}</span>
        </span>
    );
}

export function OutcomeBadge({
    tone,
    children,
    className
}: OutcomeBadgeProps): React.ReactElement {
    const toneClass = {
        success:
            "border-l-success-base bg-status-success-000 text-success-base",
        warning:
            "border-l-warning-base bg-status-warning-000 text-warning-base",
        danger: "border-l-error-base bg-status-error-000 text-error-base",
        neutral:
            "border-l-foreground-neutral bg-surface-sunken text-foreground-muted"
    }[tone];

    return (
        <span className={cn(SEMANTIC_BADGE_BASE, toneClass, className)}>
            {children}
        </span>
    );
}

export function BoundaryBadge({
    boundary,
    children,
    className
}: BoundaryBadgeProps): React.ReactElement {
    const boundaryClass = {
        delegated:
            "border-l-[rgb(var(--color-tint-base))] bg-tint/10 text-tint",
        constrained:
            "border-l-warning-base bg-status-warning-000 text-warning-base",
        locked: "border-l-error-base bg-status-error-000 text-error-base"
    }[boundary];

    return (
        <span className={cn(SEMANTIC_BADGE_BASE, boundaryClass, className)}>
            {children}
        </span>
    );
}

export function MetadataChip({
    children,
    className
}: SemanticBadgeProps): React.ReactElement {
    return (
        <span
            className={cn(
                SEMANTIC_BADGE_BASE,
                "border-l-line-dark text-foreground-muted",
                NEUTRAL_BADGE_SURFACE,
                className
            )}
        >
            {children}
        </span>
    );
}

export function CountChip({
    tone,
    children,
    className
}: CountChipProps): React.ReactElement {
    const toneClass =
        tone === "attention"
            ? "bg-accent/10 text-accent"
            : "bg-surface-sunken text-foreground-muted";

    return (
        <span
            className={cn(
                "inline-flex h-5 min-w-5 cursor-default select-none items-center justify-center rounded-none px-1.5 font-numeric text-xs leading-4 tracking-normal tabular-nums",
                toneClass,
                className
            )}
        >
            {children}
        </span>
    );
}

export function AlertBadge({
    severity,
    children,
    className
}: AlertBadgeProps): React.ReactElement {
    const severityClass = {
        critical: "border-l-error-base bg-status-error-000 text-error-base",
        review: "border-l-warning-base bg-status-warning-000 text-warning-base",
        info: "border-l-[rgb(var(--color-info-base))] bg-status-info-000 text-info-base"
    }[severity];
    const state: LifecycleStatusTone =
        severity === "critical"
            ? "error"
            : severity === "review"
              ? "pending"
              : "disabled";

    return (
        <span className={cn(SEMANTIC_BADGE_BASE, severityClass, className)}>
            <IndicationUnit variant="status" state={state} size={7} />
            {children}
        </span>
    );
}

export type {
    AlertBadgeProps,
    BoundaryBadgeProps,
    CountChipProps,
    LifecycleStatusBadgeProps,
    OutcomeBadgeProps,
    SemanticBadgeProps
};
